import { Fragment, useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Loader2, AlertTriangle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import API from "@/apiConfig";

const PAYPAL_GIVING_CLIENT_ID = process.env.REACT_APP_PAYPAL_GIVING_CLIENT_ID;

function useBalance(token) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/rentals/balance/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Payment link not found or expired."));
  }, [token]);
  return { info, setInfo, error };
}

function PayPalBalanceButtons({ token, info, onPaid }) {
  const ref = useRef(null);
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const render = () => {
      if (!window.paypal || !ref.current) return;
      ref.current.innerHTML = "";
      window.paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 },
        createOrder: (_d, actions) => actions.order.create({
          purchase_units: [{
            description: `WAGC Community Room balance: ${info.event_name} (${info.event_date})`.slice(0, 127),
            invoice_id: `wagc-rental-bal-${token}-${Date.now()}`.slice(0, 127),
            amount: { value: info.balance_due.toFixed(2) },
          }],
        }),
        onApprove: async (_d, actions) => {
          setProcessing(true);
          try {
            const details = await actions.order.capture();
            const captureId = details?.purchase_units?.[0]?.payments?.captures?.[0]?.id || "";
            await axios.post(`${API}/rentals/balance/${token}/pay`, { paypal_order_id: details.id, capture_id: captureId, amount: info.balance_due });
            toast.success("Balance paid. Thank you!");
            onPaid();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment could not be recorded. Please contact us.");
          } finally {
            setProcessing(false);
          }
        },
        onError: () => toast.error("PayPal error — please try again."),
      }).render(ref.current).then(() => setReady(true)).catch(() => {});
    };
    if (window.paypal) { render(); return; }
    let script = document.getElementById("paypal-rental-sdk");
    if (!script) {
      script = document.createElement("script");
      script.id = "paypal-rental-sdk";
      script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_GIVING_CLIENT_ID}&currency=USD`;
      document.body.appendChild(script);
    }
    script.addEventListener("load", render);
    return () => script.removeEventListener("load", render);
  }, [token, info, onPaid]);

  return (
    <div className="bg-white rounded-sm border border-bone-dark p-6" data-testid="balance-paypal-section">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="h-5 w-5 text-gold" />
        <h3 className="font-heading text-lg text-navy">Pay ${info.balance_due.toFixed(2)} via PayPal / Card</h3>
      </div>
      {processing ? (
        <div className="flex items-center justify-center gap-2 py-4 text-navy"><Loader2 className="h-5 w-5 animate-spin text-gold" /><span className="text-sm">Processing payment...</span></div>
      ) : <div ref={ref} className="min-h-[50px]" />}
      {!ready && !processing && <p className="text-xs text-gray-400 text-center py-2">Loading payment options...</p>}
    </div>
  );
}

function Summary({ info }) {
  const rows = [
    ["Event", info.event_name],
    ["Date", `${info.event_date}${info.event_time ? ` at ${info.event_time}` : ""}`],
    ["Total Rental", `$${Number(info.rental_amount).toFixed(2)}`],
    ["Already Paid", `$${info.amount_paid.toFixed(2)}`],
  ];
  return (
    <div className="bg-white rounded-sm border border-bone-dark p-5 mb-6" data-testid="balance-summary">
      <h3 className="font-heading text-base text-navy mb-3">Booking Summary</h3>
      <div className="grid grid-cols-2 gap-y-2 text-sm">
        {rows.map(([k, v]) => (<Fragment key={k}><span className="text-gray-500">{k}</span><span className="text-navy">{v}</span></Fragment>))}
        <span className="text-gray-500 font-medium">Balance Due</span>
        <span className="text-navy font-heading text-lg" data-testid="balance-due-amount">${info.balance_due.toFixed(2)}</span>
      </div>
    </div>
  );
}

export default function RentalBalancePage() {
  const { token } = useParams();
  const { info, setInfo, error } = useBalance(token);
  const markPaid = () => setInfo((i) => ({ ...i, status: "paid" }));

  let body;
  if (error) {
    body = (
      <div className="text-center py-10" data-testid="balance-error">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <h2 className="font-heading text-xl text-navy mb-2">Payment Link Invalid</h2>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    );
  } else if (!info) {
    body = <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  } else if (info.status === "paid") {
    body = (
      <div className="text-center py-10" data-testid="balance-paid">
        <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h2 className="font-heading text-2xl text-navy mb-2">Paid in Full</h2>
        <p className="text-gray-500 text-sm">Your rental{info.event_name ? ` for ${info.event_name}` : ""} is fully paid. Nothing is due on arrival.</p>
      </div>
    );
  } else {
    body = (
      <>
        <Summary info={info} />
        <PayPalBalanceButtons token={token} info={info} onPaid={markPaid} />
        <p className="text-xs text-gray-500 text-center mt-4">Prefer to pay in person? Cash or check is accepted at The Master's Blend.</p>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="balance-page">
      <div className="bg-navy py-8 text-center">
        <h1 className="font-heading text-2xl sm:text-3xl text-white">Word Alive Global Center</h1>
        <p className="text-gold text-sm mt-1">Community Room Rental Balance</p>
      </div>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-10">
        {body}
        <p className="text-center text-xs text-gray-400 mt-6">1128 W Panola Street, Carthage, TX 75633</p>
      </div>
    </div>
  );
}
